import {
  Card,
  SimpleGrid,
  Text,
  Modal,
  Button,
  TextInput,
  Textarea,
  Group,
  Select,
  Loader,
  Box,
  MultiSelect,
  ActionIcon,
  Stack,
} from '@mantine/core';
import { useDebounce } from '@/hooks/useDebounce';
import SearchBox from '@/components/SearchBox';
import TagFilter from '@/components/TagFilter';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createBot, generatePrompt, getRobotList, useCanAccess, getEnabledAgentTypes, RobotListItem } from '@mygpt/app';
import { DataFrozenTip } from '@/components/DataFrozenTip';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { Robot } from '@/components/Robot';
import { useGPTStore } from '@/store';
import { useAdminStore } from '@/store/admin';
import { VirtuosoGrid, VirtuosoGridProps } from 'react-virtuoso';

const BOT_SEARCH_STATE = 'botListSearchState';
import { getRobotTags, setRobotTags, createRobotTag } from '@mygpt/app/apis/services/robotTag';
import { setResourcePrinciple } from '@mygpt/app/apis/services/admin';
import { RobotTag } from '@mygpt/types/tag';
import { IconX, IconPlus } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';

const SVGAdd = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path
      d="M15.0625 8.03125H8.03125M8.03125 8.03125H1M8.03125 8.03125V15.0625M8.03125 8.03125L8.03125 1"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// Ensure that the component definitions are not declared inline in the component function,
// Otherwise the grid will remount with each render due to new component instances.
const gridComponents: VirtuosoGridProps<undefined, undefined>['components'] = {
  List: forwardRef(({ style, children, ...props }, ref) => {
    return (
      <SimpleGrid
        ref={ref}
        spacing="md"
        verticalSpacing="md"
        cols={{ base: 1, xs: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
        style={style}
      >
        {children}
      </SimpleGrid>
    );
  }),
  Footer: (context) => {
    return (
      <div className="flex justify-center w-full pt-7">
        <Loader color="blue" />
      </div>
    );
  },
};

const CREATE_ROBOT_PLACEHOLDER = {
  id: '',
  name: '',
} as RobotListItem;

// Session storage key for filter state
const BOT_FILTER_STATE = 'bot_filter_state';

interface FilterState {
  search: string;
  tagIds: string[];
}

const DEFAULT_AGENT_PROMPT = `# Role
You are a highly intelligent and professional assistant designed to assist users with their tasks. Your role is to provide accurate, clear, and concise responses to user queries while maintaining a friendly and helpful tone.

# General
When responding to user inputs:
1. Understand the user's request or task by analyzing the input carefully.
2. Provide a detailed and relevant response that directly addresses the user's needs.
3. If the task requires reasoning, ensure to explain the reasoning process before presenting the conclusion.
4. Maintain clarity and conciseness in your responses, avoiding unnecessary information.

# Output Format
- For simple queries, provide a direct and concise answer.
- For tasks requiring structured data, use JSON format with clearly defined fields.
- For complex tasks, break down the response into logical steps or sections for better understanding.

# Example Interaction
**User Input:** What is the capital of France?
**Assistant Response:** The capital of France is Paris.`;

const BotsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roles, assetScope, setAssetScope } = useGPTStore();
  const { companyId } = useAdminStore();
  const [bots, setBots] = useState<RobotListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // 检查是否为本地部署版且权限为ROLE_VIEWER
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  // Cloud版才需要传递asset_scope参数
  const isCloudMode = !isLocalMode;
  const isViewerOnly =
    roles && Array.isArray(roles) && roles.length > 0 && roles.every((r: any) => r.code === 'ROLE_VIEWER');

  // 搜索状态持久化函数
  const saveSearchState = (search: string) => {
    sessionStorage.setItem(BOT_SEARCH_STATE, search);
  };
  const restoreSearchState = (): string => {
    return sessionStorage.getItem(BOT_SEARCH_STATE) || '';
  };
  const clearSearchState = () => {
    sessionStorage.removeItem(BOT_SEARCH_STATE);
  };

  // 搜索状态（从 sessionStorage 恢复，支持导航返回时保持搜索词）
  const [searchValue, setSearchValue] = useState(() => restoreSearchState());
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // Tag filter state
  const [availableTags, setAvailableTags] = useState<RobotTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  // selectedTagIds 同步初始化：URL > sessionStorage > []
  // 用懒初始化避免 mount 后再异步 setState，否则会因新数组引用让主 useEffect 重复触发一次列表请求
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tagIdsParam = urlParams.get('tag_ids');
      if (tagIdsParam) {
        return tagIdsParam.split(',').filter(Boolean);
      }
      const savedFilter = sessionStorage.getItem(BOT_FILTER_STATE);
      if (savedFilter) {
        const filterState: FilterState = JSON.parse(savedFilter);
        return filterState.tagIds || [];
      }
    } catch (e) {
      console.error('Failed to init selectedTagIds:', e);
    }
    return [];
  });

  // 加载可用标签列表（不再读取 sessionStorage 的 tagIds —— 已在 useState 初始化时处理）
  useEffect(() => {
    setTagsLoading(true);
    getRobotTags()
      .then((res) => {
        setAvailableTags(res.data);
      })
      .catch((error) => {
        console.error('Failed to load tags:', error);
      })
      .finally(() => {
        setTagsLoading(false);
      });
  }, []);

  // Save filter state to session storage
  useEffect(() => {
    const filterState: FilterState = {
      search: searchValue,
      tagIds: selectedTagIds,
    };
    sessionStorage.setItem(BOT_FILTER_STATE, JSON.stringify(filterState));
  }, [searchValue, selectedTagIds]);

  // Update URL when tag filter changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedTagIds.length > 0) {
      url.searchParams.set('tag_ids', selectedTagIds.join(','));
    } else {
      url.searchParams.delete('tag_ids');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selectedTagIds]);

  // 检查是否有数据集处于冻结状态
  const frozenTipOpened = useMemo(() => {
    return bots.some((item) => {
      return item.datasets?.length && item.datasets.some((dataset) => dataset.data_status === 'frozen');
    });
  }, [bots]);

  const initialLoadDone = useRef(false);
  const loadingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  // 取消上一次列表请求：切换 assetScope / 搜索词 / 标签时使用，避免 race condition
  // 旧请求的 .then/.finally 通过 controller 引用对比跳过状态写入，避免覆盖新数据或关闭 loading
  const abortControllerRef = useRef<AbortController | null>(null);
  // setBots的包装，每次都先判断是否需要添加还是删掉「创建机器人卡片的占位数据」
  const setBotsHandler = (data: RobotListItem[]) => {
    const [first, ...others] = data;

    // 查看者在企业模式下不显示创建卡片，个人模式下可以创建
    // 本地部署版查看者也不显示创建卡片（保持原有行为）
    const shouldHideCreateCard = isViewerOnly && (isLocalMode || !!assetScope);

    if (shouldHideCreateCard) {
      // 如果用户的权限变成了查看者（企业模式或本地部署版），就把创建Bot卡片删掉

      // 如果之前已经添加了，就删掉
      if (first && first?.id === '') {
        setBots(others);
      } else {
        setBots(data);
      }
      return;
    }

    // 如果用户当前不是这个权限
    // 并且之前没有追加过，那么追加一个新增Bot按钮在头部
    if (!first || first?.id) {
      setBots(
        [
          // 默认追加一个空的
          CREATE_ROBOT_PLACEHOLDER,
        ].concat(data)
      );
    } else {
      // 之前已经追加过，直接设置
      setBots(data);
    }
  };

  // Handle search without useCallback to avoid dependency issues
  // override: 是否覆盖当前列表已查出来的bot数据（true 表示场景切换/搜索/标签变化，会 abort 上一次请求）
  const handleSearch = (searchTerm: string, page = 1, override = false, tagIds?: string[]) => {
    // 分页加载（override=false）：仍然用 loadingRef dedupe，避免 endReached 重复触发
    // 场景切换（override=true）：永远以最新一次为准，主动 abort 上一次请求
    if (!override && loadingRef.current) return;
    if (override && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    loadingRef.current = true;

    // Only search if term is empty (for clearing) or has at least 1 character
    const trimmedTerm = searchTerm.trim();
    const tagsToUse = tagIds !== undefined ? tagIds : selectedTagIds;

    if (trimmedTerm.length === 0 || trimmedTerm.length >= 1 || tagsToUse.length > 0) {
      setLoading(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;
      getRobotList(
        {
          page,
          size: 23,
          include_counts: false,
          search: trimmedTerm || undefined,
          // Cloud版传入公司ID参数：有组织ID则传入，否则不传（表示个人资产）
          ...(isCloudMode && assetScope && { company_id: assetScope }),
          tag_ids: tagsToUse.length > 0 ? tagsToUse : undefined,
        },
        { signal: controller.signal }
      )
        .then((res) => {
          // 过期请求（已被新请求替换）不写状态，避免覆盖新数据
          if (abortControllerRef.current !== controller) return;

          const { items, page, total } = res.data;
          if (!items.length && page > 1) {
            return;
          }

          // 如果是搜索关键词触发的查询列表，那么重置一次当前列表
          // 不耽误后续有搜索关键词的情况下，继续请求滚动分页
          if (override) {
            setBotsHandler(items);
            setPage(page);
            setTotal(total);
            return;
          }

          setBotsHandler(bots.concat(items));
          setPage(page);
          setTotal(total);
        })
        .catch(() => {
          // 请求被 abort 或其他错误：交由 finally 守卫处理，无需提示
        })
        .finally(() => {
          // 仅当本次是「当前」请求时才关闭 loading；否则保持新请求的 loading 状态不被旧请求误关
          if (abortControllerRef.current === controller) {
            loadingRef.current = false;
            setLoading(false);
            abortControllerRef.current = null;
          }
        });
    } else {
      loadingRef.current = false;
    }
  };

  // 搜索词或标签变化时执行查询（包含初始加载；debouncedSearchValue 初始来自 sessionStorage）
  // 先清空旧列表/分页状态，避免 VirtuosoGrid 残留旧数据触发 endReached 误加载下一页
  useEffect(() => {
    setBots([]);
    setPage(1);
    setTotal(0);
    handleSearch(debouncedSearchValue, 1, true, selectedTagIds);
    initialLoadDone.current = true;
  }, [debouncedSearchValue, selectedTagIds]);

  // Cloud 版：仅在 assetScope 真正切换时（非初始 mount）重置并重新加载
  // handleSearch 内部会 abort 上一次请求，避免快速切换时旧请求覆盖新数据
  const skipAssetScopeMountRef = useRef(true);
  useEffect(() => {
    if (skipAssetScopeMountRef.current) {
      skipAssetScopeMountRef.current = false;
      return;
    }
    if (isCloudMode) {
      // 重置列表和分页状态
      setBots([]);
      setPage(1);
      setTotal(0);
      // 重新加载数据（override=true 会 abort 上一次请求）
      handleSearch(debouncedSearchValue || '', 1, true);
    }
  }, [assetScope]);

  // 卸载时取消进行中的请求
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const isAgentDisabled = (window as any).ENV?.GBASE_SUPPORT_FRONTEND_AGENT_DISABLE === 'true';
  const enabledAgentTypes = useMemo(() => getEnabledAgentTypes(), []);
  const canAccessAgent = useCanAccess('enable_agent');
  const isAgentAvailable = !isAgentDisabled && canAccessAgent && enabledAgentTypes.length > 0;

  // Poll the list while any bot is being cloned. When transitions land we toast.
  const prevStatusesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const cloningPresent = bots.some((b) => b.ai_status === 'cloning_target' || b.ai_status === 'cloning_source');

    bots.forEach((b) => {
      const prev = prevStatusesRef.current[b.id];
      if (prev === 'cloning_target' && b.ai_status === 'ready') {
        showNotification({ message: t('clone_bot.success'), color: 'green' });
      } else if (prev === 'cloning_target' && b.ai_status === 'cloning_failed') {
        showNotification({ message: t('clone_bot.failed_toast'), color: 'red' });
      }
      prevStatusesRef.current[b.id] = b.ai_status;
    });

    if (!cloningPresent) return;
    const id = setInterval(() => {
      handleSearch(debouncedSearchValue, 1, true, selectedTagIds);
    }, 10000);
    return () => clearInterval(id);
  }, [bots, debouncedSearchValue, selectedTagIds]);

  const [modalOpened, setModalOpened] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  // 默认 Agent 优先；若环境/权限不允许 Agent，则回退为 Chatbot
  const [isChatbot, setIsChatbot] = useState(!isAgentAvailable);
  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [selectedAgentType, setSelectedAgentType] = useState(enabledAgentTypes[0]?.id || 'general_agent');

  // Tag-related state for add bot modal (different names from filter state)
  const [modalAvailableTags, setModalAvailableTags] = useState<RobotTag[]>([]);
  const [modalSelectedTagIds, setModalSelectedTagIds] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [showNewTagInput, setShowNewTagInput] = useState(false);

  // Bot 类型下拉选项：Agent 类型在前，Chatbot 沉到末位（弱化）
  const botTypeOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    if (isAgentAvailable) {
      enabledAgentTypes.forEach((type) => {
        options.push({
          value: type.id,
          label: t(`create.agent_types.${type.id}`, { defaultValue: type.name }),
        });
      });
    }
    options.push({ value: 'chatbot', label: t('create.bot_types.chatbot') });
    return options;
  }, [isAgentAvailable, enabledAgentTypes, t]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setTitleError(t('create.name_required'));
      return;
    } else {
      setTitleError('');
    }

    if (!description.trim()) {
      setDescriptionError(t('create.description_required'));
      return;
    } else {
      setDescriptionError('');
    }

    setLoadingSubmit(true);
    let promptResponse = { prompt: description };
    // catalog_agent 不调用 gen_prompt 接口
    if (!isAgentDisabled && !isChatbot && selectedAgentType !== 'catalog_agent') {
      try {
        const aiResponse = await generatePrompt({
          prompt_in: description,
          cur_prompt: null,
          stream: false,
        });
        const invalidPatterns = ['unclear', 'incomplete', 'provide more details', 'clarify your request'];
        const isInvalid = invalidPatterns.some((p) => aiResponse.prompt?.toLowerCase().includes(p));
        promptResponse = { prompt: isInvalid ? DEFAULT_AGENT_PROMPT : aiResponse.prompt };
      } catch {
        promptResponse = { prompt: DEFAULT_AGENT_PROMPT };
      }
    }
    try {
      const requestBody = {
        name: title,
        discrible: description,
        subject_name: null,
        prompt: promptResponse.prompt,
        url: null,
        dataset_ids: null,
        init_default_datasets: false,
        ai_type: 'private',
        ai_model: isChatbot ? 'digital_human' : 'agent',
        robot_type: isChatbot ? 'rag' : selectedAgentType,
        agent_function_call_ids: [],
        // Cloud版传入公司ID：有组织ID则传入，否则不传（表示个人资产）
        ...(isCloudMode && assetScope && { company_id: assetScope }),
      };
      const data = await createBot(requestBody);

      // Cloud企业模式下初始化 bot 权限为私有（仅创建者可见）
      if (assetScope) {
        try {
          const { userOgsId } = useAdminStore.getState();
          await setResourcePrinciple({
            userIds: [userOgsId || ''],
            departmentIds: [],
            companyAuthorized: false,
            dataId: data.id,
            dataType: 'BOT',
            authorityCode: 'BOT_VIEW',
          });
        } catch (e) {
          console.error('Failed to init bot permission:', e);
        }
      }

      // Save tags if any were selected
      if (modalSelectedTagIds.length > 0) {
        try {
          await setRobotTags(data.id, modalSelectedTagIds);
        } catch (tagError) {
          console.error('Failed to save tags:', tagError);
          // Don't block navigation if tag saving fails
        }
      }

      setModalOpened(false);
      if (isChatbot) {
        const queryParams = new URLSearchParams({
          // id: data.datasets[0].id,
          botId: data.id,
        }).toString();
        navigate(`/create?${queryParams}`);
      } else {
        navigate(`/bot/${data.id}/settings#prompt`);
      }
    } catch (error) {
      console.error('Failed to create bot:', error);
      setLoadingSubmit(false);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleOpenModal = () => {
    setTitle('');
    setDescription('');
    setModalSelectedTagIds([]);
    setNewTagName('');
    setShowNewTagInput(false);
    // 每次打开都重置为默认类型（Agent 优先；不可用时回退 Chatbot）
    setIsChatbot(!isAgentAvailable);
    setSelectedAgentType(enabledAgentTypes[0]?.id || 'general_agent');
    setModalOpened(true);

    // Load available tags for the modal
    getRobotTags()
      .then((res) => {
        setModalAvailableTags(res.data);
      })
      .catch((error) => {
        console.error('Failed to load tags:', error);
      });
  };

  // Handle creating a new tag from the modal
  const handleCreateNewTag = async () => {
    if (!newTagName.trim()) {
      showNotification({
        message: t('tag.name_required', 'Tag name is required'),
        color: 'red',
      });
      return;
    }

    try {
      // Generate a random color for the new tag
      const hue = Math.floor(Math.random() * 360);
      const saturation = 70 + Math.floor(Math.random() * 30);
      const lightness = 45 + Math.floor(Math.random() * 15);

      const hslToHex = (h: number, s: number, l: number) => {
        l /= 100;
        const a = (s * Math.min(l, 1 - l)) / 100;
        const f = (n: number) => {
          const k = (n + h / 30) % 12;
          const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
          return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0');
        };
        return `#${f(0)}${f(8)}${f(4)}`;
      };

      const color = hslToHex(hue, saturation, lightness);

      const result = await createRobotTag({ name: newTagName.trim(), color });
      const newTag = result.data;

      // Add to available tags and select it
      setModalAvailableTags([...modalAvailableTags, newTag]);
      setModalSelectedTagIds([...modalSelectedTagIds, newTag.id]);
      setNewTagName('');
      setShowNewTagInput(false);

      showNotification({
        message: t('tag.create_success', 'Tag created successfully'),
        color: 'green',
      });
    } catch (error: any) {
      console.error('Failed to create tag:', error);
      // Handle error with code or fallback to message
      const detail = error.response?.data?.detail;
      let errorMessage = t('tag.create_error', 'Failed to create tag');
      if (detail) {
        if (typeof detail === 'object' && detail.code) {
          errorMessage = t(`tag.${detail.code}`, detail.message) || detail.message;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      showNotification({
        message: errorMessage,
        color: 'red',
      });
    }
  };

  // Format data for MultiSelect
  const modalSelectData = useMemo(() => {
    return modalAvailableTags.map((tag) => ({
      value: tag.id,
      label: tag.name,
    }));
  }, [modalAvailableTags]);

  const handleClose = () => {
    setTitleError('');
    setDescriptionError('');
    setModalOpened(false);
  };

  // Clear search - using useCallback to prevent recreation
  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    clearSearchState();
  }, []);

  // 让渡到企业成功后的处理：
  // bot 让渡后 organization_id 被设到企业，个人视图（assetScope=null）会过滤掉它，
  // 看起来像“bot 丢失”。这里切到目标企业资产范围，[assetScope] 的 useEffect 会自动
  // 重新拉取企业列表，让渡的 bot 立即可见。传入此回调后 card.tsx 不再走
  // window.location.reload() 兜底，避免整页重载白屏。
  const handleDonated = () => {
    if (companyId) {
      const scope = String(companyId);
      if (assetScope === scope) {
        // 已在该企业视图（assetScope 不变不会触发重载），手动刷新一次
        handleSearch(debouncedSearchValue, 1, true, selectedTagIds);
      } else {
        setAssetScope(scope);
      }
    } else {
      // 无企业上下文，退化为本地刷新当前列表
      handleSearch(debouncedSearchValue, 1, true, selectedTagIds);
    }
  };

  // Handle search value change
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // Memoize SearchBox to prevent recreation
  const searchBoxElement = useMemo(
    () => (
      <SearchBox
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleClearSearch}
        placeholder={t('common.search_bots', 'Search bots...')}
        width={300}
      />
    ),
    [searchValue, handleSearchChange, handleClearSearch, t]
  );

  // Handle tag change
  const handleTagChange = useCallback((tagIds: string[]) => {
    setSelectedTagIds(tagIds);
    setPage(1);
  }, []);

  // Memoize TagFilter to prevent recreation
  const tagFilterElement = useMemo(
    () => (
      <TagFilter
        tags={availableTags}
        selectedTagIds={selectedTagIds}
        onTagChange={handleTagChange}
        loading={tagsLoading}
        width={280}
      />
    ),
    [availableTags, selectedTagIds, handleTagChange, tagsLoading]
  );

  return (
    <div className="p-5 h-full flex flex-col">
      <ContainerHeader title={t('page.my_bots_title')} description={t('page.my_bots_description')} />
      {/* Filter section - aligned to left, above the bot grid */}
      <Group gap="md" mb="md" align="flex-start">
        {searchBoxElement}
        {isLocalMode && tagFilterElement}
      </Group>
      <div className="flex-grow">
        {loading && bots.length === 0 ? (
          <div className="flex justify-center items-center w-full h-full pt-10">
            <Loader />
          </div>
        ) : (
          <VirtuosoGrid
            data={bots}
            components={{
              List: gridComponents.List,
              Footer: () => {
                if (bots.length >= total) {
                  return null;
                }
                return (
                  <div className="flex justify-center w-full pt-7">
                    <Loader />
                  </div>
                );
              },
            }}
            endReached={() => handleSearch(debouncedSearchValue, page + 1)}
            itemContent={(index, data) => {
              if (index === 0 && !data.id) {
                return (
                  <div className="h-full">
                    <Card
                      shadow="sm"
                      className="border border-solid border-gray-100 cursor-pointer hover:bg-violet-100 hover:border-violet-300 transition-all h-full"
                      onClick={handleOpenModal}
                    >
                      <section className="flex flex-col items-center justify-center gap-2 h-[208px]">
                        <div className="bg-violet-600 rounded-full p-2">
                          <SVGAdd />
                        </div>
                        <Text c={'violet'}>{t('create.chatbot')}</Text>
                      </section>
                    </Card>
                  </div>
                );
              }

              return (
                <Robot.Card
                  robot={data}
                  searchTerm={debouncedSearchValue}
                  onCloned={() => handleSearch(debouncedSearchValue, 1, true, selectedTagIds)}
                  onDonated={handleDonated}
                />
              );
            }}
          />
        )}
      </div>
      <Modal
        opened={modalOpened}
        onClose={handleClose}
        title={t('create.chatbot')}
        centered
        size="lg"
        className="rounded-lg shadow-lg max-w-3xl"
      >
        <div>
          {botTypeOptions.length > 1 && (
            <Select
              label={t('create.bot_type_label')}
              placeholder={t('create.bot_type_placeholder')}
              data={botTypeOptions}
              value={isChatbot ? 'chatbot' : selectedAgentType}
              onChange={(value) => {
                if (!value) return;
                if (value === 'chatbot') {
                  setIsChatbot(true);
                } else {
                  setIsChatbot(false);
                  setSelectedAgentType(value);
                }
              }}
              required
              mb="lg"
              allowDeselect={false}
            />
          )}
          <TextInput
            label={t('create.name_label')}
            placeholder={t('create.name_placeholder', { agent_type: isChatbot ? 'chatbot' : 'agent' })}
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            error={titleError}
            required
            mb="lg"
            maxLength={100}
            className="mb-4"
          />
          <Textarea
            label={t('create.description_label')}
            placeholder={t('create.description_placeholder', { agent_type: isChatbot ? 'chatbot' : 'agent' })}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
            error={descriptionError}
            required
            mb="lg"
            maxLength={200}
            className="mb-4"
          />

          {/* Tag Selection */}
          {isLocalMode && (
            <Stack gap="sm" mb="lg">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>
                  {t('tag.select_tags', 'Select Tags')}
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setShowNewTagInput(!showNewTagInput)}
                >
                  {t('tag.create_new', 'Create New Tag')}
                </Button>
              </Group>

              {showNewTagInput && (
                <Group gap="xs">
                  <TextInput
                    placeholder={t('tag.tag_name_placeholder', 'Enter tag name')}
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.currentTarget.value)}
                    size="sm"
                    style={{ flex: 1 }}
                    maxLength={50}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateNewTag();
                      }
                    }}
                  />
                  <Button size="sm" onClick={handleCreateNewTag} disabled={!newTagName.trim()}>
                    {t('tag.add', 'Add')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowNewTagInput(false);
                      setNewTagName('');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </Group>
              )}

              <MultiSelect
                data={modalSelectData}
                value={modalSelectedTagIds}
                onChange={setModalSelectedTagIds}
                placeholder={t('tag.select_tags_placeholder', 'Choose tags for this bot')}
                searchable
                clearable
                nothingFoundMessage={t('tag.no_tags_found', 'No tags found')}
                maxDropdownHeight={200}
              />
            </Stack>
          )}

          <Group className="justify-end">
            <Button variant="default" onClick={handleClose}>
              {t('alert_cancel')}
            </Button>
            <Button color="violet" onClick={handleSubmit} loading={loadingSubmit}>
              {t('alert_ok')}
            </Button>
          </Group>
        </div>
      </Modal>
    </div>
  );
};

// 一个包装函数，当用户权限信息还没有获取时，不渲染子节点
// 这样保证子节点在渲染时，已经获取了权限信息。这样的先后顺序的保证，避免了一些时间竞态问题
const WrappedBotsPage = () => {
  const { roles, assetScope } = useGPTStore();
  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  // 本地部署版 或 Cloud企业模式（有assetScope）需要等待权限加载完成
  // 避免roles未加载时错误显示创建Bot按钮
  if ((isLocalMode || !!assetScope) && (!roles || !roles.length)) {
    return null;
  }

  return <BotsPage />;
};
export default WrappedBotsPage;
